// ADD (OR EDIT) ONE PARTNER PERSON - the one dialog for both scopes of the
// Partners view (owner, 2026-10-16: merge the separate "add person" paths).
//
// The project roster had "+ Add supervisor" and "+ Add employee", two dialogs
// with their own fields; the all-projects view had "Add person" with a kind
// picker and project ticks. They ask the same questions, so they are this one
// dialog now, and each page keeps its own write (the project roster PATCHes
// its row; the all-projects view posts to the partner directory):
//
//   kind       Person (title on the job) / Supervisor / Executive
//   title      required for a person - users[] requires it
//   cost view  supervisors and executives, only for someone allowed to set it
//   projects   all-projects scope only: on which of the partner's projects
//
// The dialog validates and hands back the values; it writes nothing itself.
import React, { useEffect, useState } from "react";
import { Alert, Input, Modal, Select, Switch } from "antd";

export const PERSON_KINDS = [
  { value: "person", label: "Person (title on the job)" },
  { value: "supervisor", label: "Supervisor (gets the partner digest, can raise issues)" },
  { value: "executive", label: "Executive (weekly summary only)" },
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const muted = { color: "#667085", fontSize: 12 };

export const personProblem = (v, { projects = null } = {}) => {
  if (!v) return "Nothing to save";
  if (!String(v.name || "").trim()) return "A name is needed";
  if (!EMAIL_RE.test(String(v.email || "").trim())) return "A valid email is needed";
  if (v.kind === "person" && !String(v.title || "").trim()) return "A person needs a title on the job";
  if (projects && !(v.projectIds || []).length) return "Tick at least one project";
  return "";
};

/**
 * props:
 *   open, title, okText, busy, onCancel
 *   initial      { name, email, kind, title, showCost, projectIds }
 *   onSave(v)    v = { name, email (lower-cased), kind, title, showCost, projectIds }
 *   projects     [{ projectId, title }] - shows the project ticks (all-projects scope)
 *   canSetCostView   shows Cost view for supervisors and executives
 *   kindLocked   the kind may not change here (with kindLockedWhy)
 *   emailLocked  the address is the person's identity here and may not change
 *   intro        a line under the title saying what adding does on this page
 *   note         a closing line (who is told, what happens next)
 */
export default function PartnerPersonModal({
  open, title, okText = "Add", busy = false, onCancel, onSave, initial = null,
  projects = null, canSetCostView = false, kindLocked = false, kindLockedWhy = "",
  emailLocked = false, intro = null, note = null,
}) {
  const [v, setV] = useState(null);
  useEffect(() => {
    if (!open) { setV(null); return; }
    setV({
      name: "", email: "", kind: "person", title: "", showCost: false, projectIds: [],
      ...(initial || {}),
    });
  }, [open, initial]);
  if (!open || !v) return null;
  const problem = personProblem(v, { projects });
  const originalKind = initial && initial.kind;
  const set = (patch) => setV((cur) => ({ ...cur, ...patch }));
  const save = () => {
    if (problem) return;
    onSave({
      ...v,
      name: v.name.trim(),
      email: v.email.trim().toLowerCase(),
      title: String(v.title || "").trim(),
    });
  };

  return (
    <Modal open title={title} okText={okText} confirmLoading={busy} onCancel={onCancel} onOk={save} destroyOnClose
      okButtonProps={{ disabled: !!problem }}>
      <div style={{ display: "grid", gap: 8 }}>
        {intro ? <div style={{ color: "#888", fontSize: 13 }}>{intro}</div> : null}
        <Input placeholder="Full name" value={v.name} onChange={(e) => set({ name: e.target.value })} />
        <Input placeholder="Email" value={v.email} disabled={emailLocked} onChange={(e) => set({ email: e.target.value })} />
        {kindLocked ? (
          kindLockedWhy ? <Alert type="info" showIcon message={kindLockedWhy} /> : null
        ) : (
          <Select aria-label="Kind" value={v.kind} onChange={(kind) => set({ kind })} options={PERSON_KINDS} />
        )}
        {v.kind === "person" ? (
          <Input placeholder="Title (e.g. Foreman, PM)" value={v.title} onChange={(e) => set({ title: e.target.value })} />
        ) : null}
        {originalKind && v.kind !== originalKind ? (
          <Alert type="warning" showIcon
            message={v.kind === "person" ? "This removes their supervisor access" : v.kind === "supervisor" ? "Supervisor is a permission, not a label" : "Executive receives the weekly summary and nothing else"}
            description={v.kind === "person"
              ? "They stop receiving the partner digest and lose the supervisor permission on issues."
              : v.kind === "supervisor"
                ? "A supervisor can view, raise and update issues on this partner's projects, and receives the partner digest."
                : "An executive is a reader and recipient only - no operational role, no privileges, and no account is needed."} />
        ) : null}
        {v.kind === "executive" ? (
          <div style={muted}>
            An executive receives one weekly summary of every project they are on and nothing else. They are not
            offered as a responsible person, cannot be assigned work, and do not receive the day-to-day notices this
            company&apos;s supervisors get.
          </div>
        ) : null}
        {v.kind !== "person" && canSetCostView ? (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Switch size="small" checked={!!v.showCost} onChange={(showCost) => set({ showCost })} />
              <span>Cost view</span>
            </div>
            <div style={{ ...muted, marginTop: 4 }}>
              Off: their digests and reports show hours only. On: they also see rates, prices per hour and totals in
              currency.
            </div>
          </div>
        ) : null}
        {projects ? (
          <div>
            <div style={{ fontSize: 13, marginBottom: 4 }}>On which projects</div>
            {projects.map((pr) => {
              const on = (v.projectIds || []).includes(pr.projectId);
              return (
                <label key={pr.projectId} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={on}
                    onChange={() => set({
                      projectIds: on ? v.projectIds.filter((x) => x !== pr.projectId) : [...(v.projectIds || []), pr.projectId],
                    })} />
                  <span>{pr.title}</span>
                </label>
              );
            })}
          </div>
        ) : null}
        {note ? <div style={muted}>{note}</div> : null}
      </div>
    </Modal>
  );
}
